import MaterialGroundPlane from "./ground/material-ground-plane";
import MaterialGroundMix from "./ground/material-ground-mix";
import SystemDefaults from "../system-defaults";
import SlideManager from "../utils/slide-manager";
import Utils from "../utils";
import RenderManager from "./render-manager";

const SHOW_DEBUG_CANVAS = Utils.queryParams.has("debug_ground_canvas");
const SHOW_DEBUG_CAMERA = Utils.queryParams.has("debug_ground_camera");
const tmpVec = new THREE.Vector3();

class GroundPlane extends THREE.Object3D {
    _rtDepth = null;
    _rtMix = null;
    _camera = createShadowCamera(5, 0.1, 100);
    _material = null;
    _materialMix = null;
    _mesh = null;
    _needsUpdate = false;
    _mixCamera = new THREE.Camera();
    _mixPlane = null;
    _isInit = false;
    _hasData = false;
    _camHelper = false;

    isUnselectable = true;
    type = "GroundPlane";
    isGroundPlane = true;
    visible = false;

    setMaterialVisibility(visible) {
        this._materialMix.visible = visible;
        this._material.visible = visible;
    }

    init() {
        if (this._isInit) throw new Error("Already initialized.");

        const capabilities = Q3.browserCapabilities.supports.groundShadows;
        const res = capabilities.resolution;

        this._rtDepth = createRT(res.depth, res.depth, true, THREE.LinearFilter);
        this._rtMix = createRT(res.mix, res.mix, false, THREE.LinearFilter);
        this._material = new MaterialGroundPlane(
            this._rtMix, capabilities.samples,
            capabilities.directions, capabilities.quality, capabilities.size, capabilities.useGaussianBlur
        );
        this._materialMix = new MaterialGroundMix(this._rtDepth.depthTexture);
        this._mesh = createGroundGeometry(2 ** 12, this._material);
        this._mixPlane = new THREE.Mesh(new THREE.PlaneBufferGeometry(2, 2, 1, 1), this._materialMix);

        this.add(this._mesh);

        this._needsUpdate = true;
        this._isInit = true;

        if (SHOW_DEBUG_CANVAS) {
            const debugCanvas = document.createElement("canvas");
            this._dbgCtx = debugCanvas.getContext("2d");
            this._dbgImd = this._dbgCtx.createImageData(res.depth, res.depth);
            debugCanvas.width = debugCanvas.height = res.depth;
            Object.assign(debugCanvas.style, {
                transform: "scale(1, -1)",
                filter: "FlipV",
                pointerEvents: "none",
                position: "absolute",
                zIndex: "9999",
                width: "20vmin",
                height: "20vmin"
            });
            document.getElementById("viewer").appendChild(debugCanvas);
        }

        if (SHOW_DEBUG_CAMERA) {
            const helper = new THREE.CameraHelper(this._camera);
            RenderManager.root.add(helper);
            this._camHelper = helper;
        }
    }

    get needsUpdate() { return this._needsUpdate; }
    set needsUpdate(v) { this._needsUpdate = v; }

    getFieldOfView(objectPosition, objectHeight) {
        const diff = new THREE.Vector3().subVectors(objectPosition, this._camera.position);
        const distance = diff.dot(new THREE.Vector3(0, -1, 0));
        const angle = Math.atan((objectHeight * 0.5) / distance);

        return angle * 2 * THREE.Math.RAD2DEG;
    }

    /**
     * 
     * @param {THREE.WebGLRenderer} renderer 
     * @param {THREE.Scene} scene 
     * @param {THREE.Box3} sceneBoundingBox
     */
    render(renderer, scene, sceneBoundingBox) {
        if (!this._needsUpdate) return;

        const defaults = SystemDefaults.defaultShadowSettings();
        const slide = SlideManager.currentSlide;
        const shadowSettings = slide.groundShadow || {};
        const enabled = "enabled" in shadowSettings ? shadowSettings.enabled : defaults.enabled;

        const oldRt = renderer.getRenderTarget();
        const oldClearColor = renderer.getClearColor(), oldClearAlpha = renderer.getClearAlpha();

        if (!enabled) {
            if (this._hasData) {
                renderer.setRenderTarget(this._rtDepth);
                renderer.setClearColor(0x000000, 0);
                renderer.clear();

                renderer.setRenderTarget(this._rtMix);
                renderer.setClearColor(0x000000, 0);
                renderer.clear();
            }
        } else {
            const color = "color" in shadowSettings ? shadowSettings.color : defaults.color;
            const opacity = "opacity" in shadowSettings ? shadowSettings.opacity : defaults.opacity;
            const radius = "radius" in shadowSettings ? shadowSettings.radius : defaults.radius;
            const position = ("position" in shadowSettings ? shadowSettings.position : defaults.position) + sceneBoundingBox.min.y;

            this._mesh.visible = false;
            sceneBoundingBox.getCenter(tmpVec);


            this._camera.position.set(tmpVec.x, this._camera.far + position, tmpVec.z);

            const objectPosition = new THREE.Vector3().copy(tmpVec);

            sceneBoundingBox.getSize(tmpVec);
            const maxAxis = Math.max(...tmpVec.toArray());

            const fov = this.getFieldOfView(objectPosition, maxAxis) + 1;
            if (this._camera.fov !== fov) {
                this._camera.fov = fov;
                this._camera.updateProjectionMatrix();
                if (this._camHelper) this._camHelper.update();
            }

            const renderedAsset = [];
            scene.traverseVisible(object => {
                if (!object.isMesh || !object.visible || !Q3.data.assets.objects[object.uuid]) return;

                const data = SlideManager.currentSlide.animation[object.uuid];

                if ("shadowEnabled" in data && data.shadowEnabled === true) return;

                object.visible = false;
                renderedAsset.push(object);
            });

            this._mesh.position.setY(position);
            this._camera.position.setY(this._camera.far - 1 + position);
            this._material.shadowRadius = radius;
            this._material.shadowColor.setStyle(color);
            this._material.shadowOpacity = opacity;
            this._material.updateCamera(this._camera);
            this._materialMix.updateCamera(this._camera);

            renderer.setRenderTarget(this._rtDepth);

            if (!SHOW_DEBUG_CANVAS) renderer.context.colorMask(false, false, false, false);

            renderer.setClearColor(0x000000, 0);
            renderer.clear();
            renderer.render(scene, this._camera, this._rtDepth, false);

            if (!SHOW_DEBUG_CANVAS) renderer.context.colorMask(true, true, true, true);

            if (SHOW_DEBUG_CANVAS) {
                const imd = this._dbgImd;
                const ctx = this._dbgCtx;

                const gl = renderer.getContext();
                const capabilities = Q3.browserCapabilities.supports.groundShadows;
                const res = capabilities.resolution;

                gl.readPixels(0, 0, res.depth, res.depth, gl.RGBA, gl.UNSIGNED_BYTE, imd.data);
                ctx.putImageData(imd, 0, 0);
            }

            renderer.setRenderTarget(this._rtMix);
            renderer.setClearColor(0x000000, 0);
            renderer.clear();
            renderer.render(this._mixPlane, this._mixCamera, this._rtMix, false);

            renderedAsset.forEach(object => object.visible = true);
        }

        renderer.setRenderTarget(oldRt);
        renderer.setClearColor(oldClearColor);
        renderer.setClearAlpha(oldClearAlpha);

        this._needsUpdate = false;
        this._mesh.visible = true;
        this._hasData = enabled;
    }
}

export default GroundPlane;
export { GroundPlane };

function createGroundGeometry(planeSize, material) {
    const geometry = new THREE.PlaneBufferGeometry(...new Array(2).fill(planeSize), 32, 32);

    geometry.rotateX(-Math.PI * 0.5);

    return new THREE.Mesh(geometry, material);
}

function createShadowCamera(fov, near, far) {
    const camera = new THREE.PerspectiveCamera(fov, 1, near, far);

    camera.position.set(0, far, 0);
    camera.rotateX(-Math.PI * 0.5);

    return camera;
}

function createRT(width, height, useDepth, filter) {
    const rt = new THREE.WebGLRenderTarget(width, height);

    rt.texture.format = THREE.RGBAFormat;
    rt.texture.minFilter = filter;
    rt.texture.magFilter = filter;
    rt.texture.generateMipmaps = false;
    rt.stencilBuffer = false;
    rt.depthBuffer = useDepth;

    if (useDepth) {
        rt.depthTexture = new THREE.DepthTexture(
            width, height,
            THREE.UnsignedShortType, undefined,
            undefined, undefined,
            filter, filter
        );
    }

    return rt;
}